import React from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { EditProps } from 'components/shared/edit';
import { Form, Field } from 'react-final-form';
import TooltipDto from './types/TooltipDto';
import { TextArea, Toggle } from 'components/shared/edit/fields';
import AxiosHelper from 'services/AxiosHelper';
import { AxiosError } from 'axios';
import { FORM_ERROR, FormSubscription } from 'final-form';
import Warning from 'components/shared/Warning';
import withEditContext from 'components/shared/edit/withEditContext';
import { OnChange } from 'react-final-form-listeners';
import WhenFieldChanges from 'components/shared/edit/fields/WhenFieldChanges';
import Spinner from 'components/shared/Spinner';
import { setTooltipPromiseByKey, clearTooltipCache } from 'services/EntityDefinitionService';
import NavigationPrompt from 'react-router-navigation-prompt';
import ModalClose from 'components/shared/ModalClose';
import Confirm from 'components/shared/Confirm';
import { firstCharacterToUpper } from 'util/StringUtil';
import ErrorAlert from '../ErrorAlert';
import { EntityDefinition } from 'types';
import ValidationErrorAlert, { AlertType } from '~/components/shared/edit/ValidationErrorAlert';

/**
 * Tooltip Edit Overlay component state
 */
interface TooltipEditOverlayState {
	initialValues: TooltipDto;
	validationError: boolean;
	saveError: AxiosError | null;
	isTypeSpecific: boolean;
	isSaving: boolean;
	wasOpened: boolean;
}

/**
 * Tooltip Edit Overlay component props
 */
interface TooltipEditOverlayProps {
	isOpen: boolean;
	toggle: () => void;
	name: string;
	label: string;
	tooltipData: { [name: string]: { fieldTooltip: TooltipDto; universalTooltip: TooltipDto } };
	entityDefinition: EntityDefinition;
	subscription?: FormSubscription;
}

/**
 * Tooltip Edit Overlay component
 * On the edit form, the local and global tooltip data for the current field can be created/edited.
 * Triggered by the TooltipEditButton compnent if the AppState.user.isTooltipEditMode is true.
 */
class TooltipEditOverlay extends React.Component<TooltipEditOverlayProps & EditProps, TooltipEditOverlayState> {
	private mounted: boolean;

	constructor(props) {
		super(props);

		this.resetState(true);
	}

	private resetState(fromConstructor: boolean): void {
		if (!fromConstructor && !this.state.wasOpened) {
			return;
		}

		const { tooltipData, name } = this.props;

		const fieldTooltip = tooltipData && tooltipData[name] && tooltipData[name].fieldTooltip;
		const universalTooltip = tooltipData && tooltipData[name] && tooltipData[name].universalTooltip;

		const isTypeSpecific = fieldTooltip && fieldTooltip.entityTypeId ? true : false;

		const state = {
			validationError: false,
			saveError: null,
			isTypeSpecific,
			isSaving: false,
			initialValues: {
				/* PLEASE INVESTIGATE AND FIX */
				/** @ts-expect-error - INCORRECT SPREAD USAGE - `oldEntityTypeId` is ALWAYS OVERWRITTEN by the following "...SPREAD" OPERATION */
				oldEntityTypeId: fieldTooltip ? fieldTooltip.entityTypeId : null,
				/** @ts-expect-error - INCORRECT SPREAD USAGE - `isUniversal` is ALWAYS OVERWRITTEN by the following "...SPREAD" OPERATION */
				isUniversal: !isTypeSpecific,
				/** @ts-expect-error - INCORRECT SPREAD USAGE - `isTypeSpecific` is ALWAYS OVERWRITTEN by the following "...SPREAD" OPERATION */
				isTypeSpecific,
				...(isTypeSpecific ? fieldTooltip : universalTooltip),
				fieldName: firstCharacterToUpper(name),
				localText: fieldTooltip && fieldTooltip.text,
				globalText: universalTooltip && universalTooltip.text,
			},
		};

		if (fromConstructor) {
			// eslint-disable-next-line
			this.state = {
				...state,
				wasOpened: false,
			};
		} else {
			this.setState({ ...state });
		}
	}

	public componentDidMount() {
		this.mounted = true;
	}

	public componentWillUnmount() {
		this.mounted = false;
	}

	private onIsTypeSpecificChange = (isTypeSpecific: boolean): void => {
		this.setState({ isTypeSpecific });
	};

	private save = (formValues: any) => {
		const { editContext, toggle, name, tooltipData } = this.props;
		const { isTypeSpecific } = this.state;

		const fieldTooltip = tooltipData[name] && tooltipData[name].fieldTooltip;
		const universalTooltip = tooltipData[name] && tooltipData[name].universalTooltip;

		const id = isTypeSpecific ? fieldTooltip && fieldTooltip.id : universalTooltip && universalTooltip.id;
		const text = isTypeSpecific ? formValues.localText : formValues.globalText;

		//#region Commerce
		let attributeCode = '';
		const anyDefinition = editContext.entityDefinition as any;
		if (isTypeSpecific && anyDefinition.attributeCode && anyDefinition.attributeCode !== '') {
			attributeCode = anyDefinition.attributeCode;
		}
		//#endregion

		const values = {
			...formValues,
			entityTypeId: isTypeSpecific ? editContext.entityTypeId : null,
			isUniversal: !isTypeSpecific,
			text,
			//#region Commerce
			attributeCode,
			//#endregion
		};

		if (id) {
			values.id = id;
		} else {
			delete values.id;
		}

		console.log('Saving tooltipd data', values);

		const url = `/ajax/tooltips/${values.id ? values.id : ''}`;

		this.setState({ isSaving: true });

		// a promise must be returned for react-final-form.
		AxiosHelper.post(url, values)
			.then(() => {
				// prevent attempting to setState after the component has unmounted.
				if (!this.mounted) {
					return;
				}

				toggle();

				if (isTypeSpecific) {
					// invalidate tooltip cache for the current entity type
					setTooltipPromiseByKey(this.props.entityDefinition.code, undefined);
				} else {
					// invalidate tooltip cache for all the entity types
					clearTooltipCache();
				}

				// reload the edit form tooltip data
				editContext.getTooltipsForEntity();

				// reset form initial values
				this.setState({
					initialValues: {
						...values,
						entityTypeId: isTypeSpecific ? editContext.entityTypeId : null,
						oldEntityTypeId: isTypeSpecific ? editContext.entityTypeId : null,
						isUniversal: !isTypeSpecific,
					},
					isSaving: false,
				});
			})
			.catch((error: AxiosError) => {
				// prevent attempting to setState after the component has unmounted.
				if (!this.mounted) {
					return;
				}

				console.log('Unable to save tooltip', error.message);

				// validation error
				if (error && error.response && error.response.status === 400) {
					const newValidationState = Object.keys(error.response.data).reduce((acc, value) => {
						const errorMessage = error.response && error.response.data[value];
						acc[value] = errorMessage;

						if (value === 'text') {
							// we use two fields here (client side) for the text (server side),
							// so we need to update acc to highlight the validation messages
							let newValue = 'localText';
							acc[newValue] = errorMessage;
							newValue = 'globalText';
							acc[newValue] = errorMessage;
						}

						return acc;
					}, {});

					this.setState({ validationError: true, isSaving: false });

					return { ...newValidationState, [FORM_ERROR]: 'An error occurred while saving.' };
				} else {
					// prevent attempting to setState after the component has unmounted.
					if (!this.mounted) {
						return;
					}

					// generic error.
					console.log('An error occurred while attempting to save:', error.message);

					this.setState({ saveError: error, isSaving: false });

					throw error;
				}
			});
	};

	public render() {
		const {
			// by default, subscribe to these only.
			subscription = { pristine: true, dirty: true, submitError: true },
			isOpen,
			toggle,
			name,
			label,
			entityDefinition,
		} = this.props;

		const { initialValues, saveError, isTypeSpecific, validationError, isSaving } = this.state;

		return (
			<Modal
				isOpen={isOpen}
				toggle={toggle}
				onOpened={() => this.resetState(false)}
				onClosed={() => this.setState({ wasOpened: true })}
			>
				<ModalClose toggle={toggle} />
				<ModalHeader>{`${initialValues && initialValues.id ? 'Edit ' : 'Create '}`}Tooltip</ModalHeader>

				<Form
					onSubmit={this.save}
					subscription={subscription}
					initialValues={initialValues}
					render={(props: any) => {
						// this portion is rendered withing the React Final Form
						// therefore the props we're expecting are coming from there
						// some of those are default, some come based on the subscriptions above
						// https://github.com/final-form/react-final-form#subscription-fieldsubscription
						const { handleSubmit, dirty, submitError } = props;

						return (
							<form onSubmit={handleSubmit} className="edit-form">
								<ModalBody>
									{saveError && (
										<ErrorAlert
											message="An error occurred while attempting to save tooltip data."
											error={saveError}
										/>
									)}
									<ValidationErrorAlert
										submitError={
											submitError
												? submitError
												: validationError
												? 'An error occurred while attempting to save tooltip data'
												: null
										}
										alertType={AlertType.Edit}
									/>

									<NavigationPrompt when={dirty}>
										{({ isActive, onConfirm, onCancel }) => (
											<Confirm
												onCancel={onCancel}
												isOpen={isActive}
												onConfirm={onConfirm}
												renderTitle={() => 'Warning'}
												renderContents={() =>
													'Are you sure you want to leave this page? Your changes will be lost.'
												}
												confirmButtonText="Leave"
												cancelButtonText="Stay"
											/>
										)}
									</NavigationPrompt>

									{!isTypeSpecific && (
										<Field
											label={`What does '${label || name}' mean?`}
											name="globalText"
											component={TextArea}
											hideTooltip={true}
											rows={7}
										/>
									)}
									{isTypeSpecific && (
										<Field
											label={`What does '${label || name}' mean specific to ${
												entityDefinition.pluralName
											}?`}
											name="localText"
											component={TextArea}
											hideTooltip={true}
											rows={7}
										/>
									)}
									<WhenFieldChanges
										field="globalText"
										set="localText"
										to={(fieldValue) =>
											initialValues.localText ? initialValues.localText : fieldValue
										}
										requireUserConfirmation={false}
									/>
									<Field name="isTypeSpecific" subscription={{ value: true }}>
										{(filedProps) => {
											return (
												<>
													{!filedProps.input.value && !initialValues.oldEntityTypeId && (
														<Warning
															message={`Updating this tooltip will change "${
																label || name
															}" everywhere, unless it is overridden.`}
														/>
													)}
													{!filedProps.input.value && initialValues.oldEntityTypeId && (
														<Warning
															message={
																<>
																	<p>
																		Updating this tooltip will change{' '}
																		{label || name} everywhere, unless it is
																		overridden.
																	</p>
																	<p>This change will clear your current override.</p>
																</>
															}
														/>
													)}
												</>
											);
										}}
									</Field>
									<Field
										label="Update tooltip only for this page"
										name="isTypeSpecific"
										component={Toggle}
										hideTooltip={true}
									/>
									<OnChange name="isTypeSpecific">
										{(fieldValue) => {
											this.onIsTypeSpecificChange(fieldValue ? true : false);
										}}
									</OnChange>
								</ModalBody>
								<ModalFooter>
									{isSaving && <Spinner />}&nbsp;
									<Button color="primary-alt" outline onClick={toggle}>
										Cancel
									</Button>
									&nbsp;
									<Button color="primary">Save</Button>
								</ModalFooter>
							</form>
						);
					}}
				/>
			</Modal>
		);
	}
}

export default withEditContext(TooltipEditOverlay);
